import React from "react";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { isEmpty, map } from "lodash";
import { AICC_LANGUAGE_STATUS_COLOR, AICC_SUMMARY_EMPTY } from "constants/aiccLanguage";
import { AiccManagedLanguage } from "types/model/aiccLanguage";
import { LANGUAGE_REFRESHING } from "../configs";
import { languageSummaryEntries } from "../languageStatus";
import styles from "./LanguagePanel.module.scss";

interface Props {
  language: AiccManagedLanguage;
  /** This one language is being re-read after an action of its own. */
  isRefreshing?: boolean;
}

/**
 * Identity only, now that each version states its own status, audience and actions on its own row.
 *
 * The summary answers the one question worth answering while the card is shut — what end users are
 * on, and whether a replacement is being prepared. The arrow is what says the copy is that
 * replacement rather than merely another version, and each number is tinted by the state it is in,
 * which is the same colour that version's dot carries once the card is open.
 */
function LanguagePanelHeader({ language, isRefreshing }: Props) {
  const entries = languageSummaryEntries(language);

  return (
    <div className={styles.panelHeader}>
      <span className={styles.panelHeaderName}>{language.name}</span>

      <span className={styles.panelHeaderSummary}>
        {isEmpty(entries)
          ? AICC_SUMMARY_EMPTY
          : map(entries, (entry, index) => (
              <React.Fragment key={entry.state}>
                {index > 0 && <span className={styles.summaryArrow}>→</span>}
                {/* Coloured by the state it is in, from the same map as that version's own dot. */}
                {!!entry.versionLabel && (
                  <span style={{ color: AICC_LANGUAGE_STATUS_COLOR[entry.status] }}>{entry.versionLabel}</span>
                )}
                {entry.state}
              </React.Fragment>
            ))}
      </span>

      {/*
        Next to the summary the action changed, not over the card: the numbers beside it are exactly
        what is being re-read, and a card that greys out cannot be read while it updates.
      */}
      {isRefreshing && (
        <span className={styles.syncing}>
          <Spin size="small" indicator={<LoadingOutlined spin />} />
          {LANGUAGE_REFRESHING}
        </span>
      )}
    </div>
  );
}

export default LanguagePanelHeader;
